import Share from "@/components/Share/Share";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Link } from "@tanstack/react-router";
import { shareDataFormatter } from "@/utils/challengeShare";
import { bgOverlayStyle } from "@/utils/helpers";
import { getDateRangeStatus } from "@/utils/challenge";
import { ChallengeType } from "@/types/challenge";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

type NoneActiveChallengeCardProps = {
  challenge: ChallengeType;
  light?: boolean;
  handleSuccess?: () => void;
};
export const NoneActiveChallengeCard = ({
  challenge,
  light,
  handleSuccess,
}: NoneActiveChallengeCardProps) => {
  const shareData = shareDataFormatter(challenge);
  const { t } = useTranslation();
  const getUrl = useMemo(() => {
    if (challenge.challenge_picture) {
      return challenge.challenge_picture?.minio_url;
    }
    return challenge.program.main_image?.minio_url;
  }, [challenge]);

  return (
    <Link
      to={`/challenge/${challenge.id}`}
      id="challenge-card"
      style={{
        backgroundImage: light ? undefined : bgOverlayStyle(getUrl),
        backgroundColor: light ? "#D4F376" : undefined,
      }}
      className={cn(
        "flex min-h-[480px] w-[295px] shrink-0 flex-col justify-between rounded-2xl bg-cover bg-center",
        {
          grayscale: challenge.state === "Closed",
        },
      )}
    >
      <div className="flex flex-row gap-2 p-6 font-bold text-white">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="25"
          viewBox="0 0 24 25"
          fill="none"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8 1.5C8.55229 1.5 9 1.94772 9 2.5V3.5H15V2.5C15 1.94772 15.4477 1.5 16 1.5C16.5523 1.5 17 1.94772 17 2.5V3.50163C17.4755 3.50489 17.891 3.51471 18.2518 3.54419C18.8139 3.59012 19.3306 3.68868 19.816 3.93597C20.5686 4.31947 21.1805 4.93139 21.564 5.68404C21.8113 6.16937 21.9099 6.68608 21.9558 7.24817C22 7.78936 22 8.45372 22 9.25868V17.7413C22 18.5463 22 19.2106 21.9558 19.7518C21.9099 20.3139 21.8113 20.8306 21.564 21.316C21.1805 22.0686 20.5686 22.6805 19.816 23.064C19.3306 23.3113 18.8139 23.4099 18.2518 23.4558C17.7106 23.5 17.0463 23.5 16.2413 23.5H7.75868C6.95372 23.5 6.28936 23.5 5.74817 23.4558C5.18608 23.4099 4.66937 23.3113 4.18404 23.064C3.43139 22.6805 2.81947 22.0686 2.43597 21.316C2.18868 20.8306 2.09012 20.3139 2.04419 19.7518C1.99998 19.2106 1.99999 18.5463 2 17.7413V9.2587C1.99999 8.45373 1.99998 7.78937 2.04419 7.24817C2.09012 6.68608 2.18868 6.16937 2.43597 5.68404C2.81947 4.93139 3.43139 4.31947 4.18404 3.93597C4.66937 3.68868 5.18608 3.59012 5.74818 3.54419C6.10898 3.51471 6.52454 3.50489 7 3.50163V2.5C7 1.94772 7.44772 1.5 8 1.5ZM7 5.50176C6.55447 5.50489 6.20463 5.51356 5.91104 5.53755C5.47262 5.57337 5.24842 5.6383 5.09202 5.71799C4.7157 5.90973 4.40973 6.21569 4.21799 6.59202C4.1383 6.74842 4.07337 6.97262 4.03755 7.41104C4.00078 7.86113 4 8.44342 4 9.3V9.5H20V9.3C20 8.44342 19.9992 7.86113 19.9624 7.41104C19.9266 6.97262 19.8617 6.74842 19.782 6.59202C19.5903 6.2157 19.2843 5.90973 18.908 5.71799C18.7516 5.6383 18.5274 5.57337 18.089 5.53755C17.7954 5.51356 17.4455 5.50489 17 5.50176V6.5C17 7.05228 16.5523 7.5 16 7.5C15.4477 7.5 15 7.05228 15 6.5V5.5H9V6.5C9 7.05228 8.55229 7.5 8 7.5C7.44772 7.5 7 7.05228 7 6.5V5.50176ZM20 11.5H4V17.7C4 18.5566 4.00078 19.1389 4.03755 19.589C4.07337 20.0274 4.1383 20.2516 4.21799 20.408C4.40973 20.7843 4.7157 21.0903 5.09202 21.282C5.24842 21.3617 5.47262 21.4266 5.91104 21.4624C6.36113 21.4992 6.94342 21.5 7.8 21.5H16.2C17.0566 21.5 17.6389 21.4992 18.089 21.4624C18.5274 21.4266 18.7516 21.3617 18.908 21.282C19.2843 21.0903 19.5903 20.7843 19.782 20.408C19.8617 20.2516 19.9266 20.0274 19.9624 19.589C19.9992 19.1389 20 18.5566 20 17.7V11.5Z"
            fill="#EDEAE4"
          />
        </svg>
        {getDateRangeStatus(challenge.start_date as string, challenge.end_date as string)}
      </div>
      <h3
        className={cn("scroll-m-20 text-2xl font-[650] tracking-tight text-light px-6 h-[106px]", {
          "text-foreground": light,
        })}
      >
        {challenge.name}
      </h3>
      <div className="flex flex-row items-center gap-2.5 p-2">
        {challenge.state === "Not started" ? (
          <>
            <Link to={`/challenge/${challenge.id}`} aria-label="Go to challenge" className="w-full">
              <Button
                variant="black"
                className={cn("h-[54px] w-full px-5 py-4 font-bold md:min-w-[170px]", {
                  "bg-foreground text-light": light,
                })}
              >
                {t("challenge.edit")}
              </Button>
            </Link>
            <Share shareData={shareData} programId={challenge.id} onSuccess={handleSuccess}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="22"
                viewBox="0 0 20 22"
                fill="hsla(40, 20%, 91%, 1)"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M16 2C14.8954 2 14 2.89543 14 4C14 4.3473 14.0885 4.67393 14.2442 4.95855C14.2544 4.97399 14.2642 4.9898 14.2737 5.00597C14.2831 5.02203 14.2919 5.03823 14.3003 5.05457C14.6532 5.62215 15.2825 6 16 6C17.1046 6 18 5.10457 18 4C18 2.89543 17.1046 2 16 2ZM13.1581 6.81485C13.883 7.54668 14.8885 8 16 8C18.2091 8 20 6.20914 20 4C20 1.79086 18.2091 0 16 0C13.7909 0 12 1.79086 12 4C12 4.37702 12.0522 4.74185 12.1497 5.08768L6.84194 8.18515C6.11704 7.45332 5.11146 7 4 7C1.79086 7 0 8.79086 0 11C0 13.2091 1.79086 15 4 15C5.11167 15 6.11741 14.5465 6.84234 13.8144L12.1509 16.9079C12.0526 17.255 12 17.6214 12 18C12 20.2091 13.7909 22 16 22C18.2091 22 20 20.2091 20 18C20 15.7909 18.2091 14 16 14C14.8902 14 13.886 14.452 13.1613 15.1819L7.8505 12.0871C7.9479 11.7415 8 11.3768 8 11C8 10.623 7.94784 10.2581 7.85034 9.91232L13.1581 6.81485ZM5.6997 9.94543C5.70808 9.96177 5.71695 9.97798 5.72631 9.99403C5.73575 10.0102 5.74557 10.026 5.75576 10.0414C5.91147 10.3261 6 10.6527 6 11C6 11.3473 5.91148 11.6739 5.75576 11.9585C5.74546 11.9742 5.73553 11.9902 5.72599 12.0065C5.71674 12.0224 5.70798 12.0384 5.6997 12.0546C5.3468 12.6221 4.71753 13 4 13C2.89543 13 2 12.1046 2 11C2 9.89543 2.89543 9 4 9C4.71753 9 5.3468 9.37785 5.6997 9.94543ZM14.2153 17.0964C14.24 17.064 14.2629 17.0296 14.284 16.9935C14.3042 16.9588 14.3221 16.9235 14.3377 16.8875C14.6966 16.3523 15.3072 16 16 16C17.1046 16 18 16.8954 18 18C18 19.1046 17.1046 20 16 20C14.8954 20 14 19.1046 14 18C14 17.6749 14.0776 17.3678 14.2153 17.0964Z"
                />
              </svg>
            </Share>
          </>
        ) : (
          <div className="h-[54px]"></div>
        )}
      </div>
    </Link>
  );
};
